/*
 * Palo Alto — reveal on scroll.
 *
 * Fades elements in as they enter the viewport. Deliberately the only motion
 * script in the theme.
 *
 * The safety property that matters: the hidden state lives behind `.pa-js`,
 * which is set by an inline script in the head. If this file fails to load, or
 * IntersectionObserver is unavailable, or anything throws, the class is removed
 * (or never applied) and every element is simply visible. Content must never
 * depend on an animation succeeding.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  function revealAll() {
    // Give up on the animation, not on the content.
    root.classList.remove('pa-js');
  }

  try {
    if (!('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      revealAll();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pa-reveal--in');
          observer.unobserve(entry.target);
        });
      },
      {
        // Start the fade slightly before the element reaches the fold so it has
        // finished by the time it is properly in view.
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.05,
      }
    );

    /*
     * Index the children of a stagger group so they cascade instead of landing
     * together. Capped at STAGGER_MAX: a twelve-tile grid indexed straight
     * through would leave the last tile a full second behind the first, which
     * reads as a page that has stopped working rather than as choreography.
     */
    var STAGGER_MAX = 5;

    function stagger(scope) {
      (scope || document).querySelectorAll('[data-pa-stagger]').forEach(function (group) {
        var items = group.querySelectorAll(':scope > .pa-reveal');
        Array.prototype.forEach.call(items, function (el, i) {
          el.style.setProperty('--pa-reveal-i', Math.min(i, STAGGER_MAX));
        });
      });
    }

    function observe(scope) {
      stagger(scope);
      (scope || document).querySelectorAll('.pa-reveal:not(.pa-reveal--in)').forEach(function (el) {
        // Anything already in view on load is revealed immediately rather than
        // waiting for a scroll that may never happen on a short page.
        var box = el.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) {
          el.classList.add('pa-reveal--in');
          return;
        }
        observer.observe(el);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        observe();
      });
    } else {
      observe();
    }

    /*
     * Cues. A cue is a marker in the page that switches a class on while it is
     * in view and off again when it leaves, so a sequence keyed to it replays
     * every time it is scrolled back to.
     *
     * Deliberately not the reveal observer above. That one fires once and
     * unobserves, which is right for copy arriving -- text that re-hid itself
     * every time you scrolled back would be maddening to read. A staged
     * sequence is the opposite: scrolling back up and watching it again is the
     * point.
     */
    var popScrollLocked = false;
    var popScrollToken = 0;
    var popScrollTimer = 0;
    var popLockedScrollY = 0;
    var popPreviousScrollBehavior = '';
    var scrollKeys = {
      ArrowDown: true,
      ArrowUp: true,
      End: true,
      Home: true,
      PageDown: true,
      PageUp: true,
      ' ': true,
    };

    function stopPopScroll(event) {
      if (!popScrollLocked) return;
      if (event.type === 'keydown' && !scrollKeys[event.key]) return;
      event.preventDefault();
    }

    function releasePopScroll(token) {
      if (token !== undefined && token !== popScrollToken) return;
      popScrollLocked = false;
      window.clearTimeout(popScrollTimer);
      var restoreY = popLockedScrollY;
      document.body.classList.remove('pa-pop-scroll-locked');
      document.body.style.removeProperty('--pa-pop-lock-top');
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, restoreY);
      root.style.scrollBehavior = popPreviousScrollBehavior;
    }

    function animationTimeMs(value) {
      value = String(value || '').trim();
      if (!value) return 0;
      if (value.slice(-2) === 'ms') return parseFloat(value) || 0;
      if (value.slice(-1) === 's') return (parseFloat(value) || 0) * 1000;
      return 0;
    }

    function cardPopTimeMs(cards) {
      var longest = 0;
      Array.prototype.forEach.call(cards, function (card) {
        var style = window.getComputedStyle(card);
        var durations = style.animationDuration.split(',');
        var delays = style.animationDelay.split(',');
        var count = Math.max(durations.length, delays.length);
        for (var index = 0; index < count; index += 1) {
          var duration = animationTimeMs(durations[index % durations.length]);
          var delay = animationTimeMs(delays[index % delays.length]);
          longest = Math.max(longest, duration + delay);
        }
      });
      return longest;
    }

    function lockThroughCardPop(cue) {
      if (popScrollLocked) return;
      popScrollLocked = true;
      popScrollToken += 1;
      var token = popScrollToken;
      popLockedScrollY = window.scrollY;
      popPreviousScrollBehavior = root.style.scrollBehavior;
      document.body.style.setProperty('--pa-pop-lock-top', -popLockedScrollY + 'px');
      document.body.classList.add('pa-pop-scroll-locked');

      // Wait until the cue class has produced its CSS animations, then release
      // from the animations themselves. No duration is duplicated in JS.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (token !== popScrollToken) return;
          var stage = cue.nextElementSibling;
          var cards = stage ? stage.querySelectorAll('.pa-overlay-card') : [];
          var animations = [];
          var fallbackTime = cardPopTimeMs(cards);

          // Some mobile browsers omit CSS animations from getAnimations().
          // Derive the safety deadline from the live CSS instead of making the
          // customer wait through a fixed multi-second timeout after the cards
          // have already landed.
          popScrollTimer = window.setTimeout(function () {
            releasePopScroll(token);
          }, Math.max(100, Math.ceil(fallbackTime) + 80));

          Array.prototype.forEach.call(cards, function (card) {
            animations = animations.concat(card.getAnimations ? card.getAnimations() : []);
          });
          if (!animations.length) {
            releasePopScroll(token);
            return;
          }
          Promise.allSettled(
            animations.map(function (animation) {
              return animation.finished;
            })
          ).then(function () {
            releasePopScroll(token);
          });
        });
      });
    }

    window.addEventListener('wheel', stopPopScroll, { passive: false });
    window.addEventListener('touchmove', stopPopScroll, { passive: false });
    window.addEventListener('keydown', stopPopScroll, { passive: false });

    var cues = document.querySelectorAll('[data-pa-cue]');
    if (cues.length) {
      var cueObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var wasOn = entry.target.classList.contains('pa-cue--on');
            entry.target.classList.toggle('pa-cue--on', entry.isIntersecting);
            // The hero dims and hands over its copy while the cards are up.
            // The hero sits BEFORE the cue in the page and CSS cannot select
            // backwards, so it was done with body:has() -- which silently
            // does nothing where :has() is missing. A body class works in
            // every browser this script runs in.
            if (entry.target.classList.contains('pa-grid-overlay__cue')) {
              document.body.classList.toggle('pa-cards-up', entry.isIntersecting);
              if (entry.isIntersecting && !wasOn) {
                lockThroughCardPop(entry.target);
              } else if (!entry.isIntersecting && !popScrollLocked) {
                popScrollToken += 1;
                releasePopScroll();
              }
            }
          });
        },
        /*
         * The top margin is the latch, and it is doing something specific.
         *
         * Extending the root upward by a large distance means the cue counts
         * as intersecting for the whole page below it: on once reached, and
         * still on however far down the reader goes. Off again only when the
         * cue is genuinely back under the fold, which is to say back at the
         * top.
         *
         * Reading the geometry inside the callback instead does not work, and
         * fails in a way that is easy to miss. IntersectionObserver only calls
         * you when intersection CHANGES. Jump straight from the bottom of the
         * page to the top -- an anchor, a scroll-to-top, a restored position --
         * and the cue goes from above the viewport to below it in one frame,
         * false to false, so nothing fires and the sequence stays latched on
         * at the top of the page. Rolled into rootMargin it is one boolean
         * that cannot get out of step.
         */
        { rootMargin: '10000px 0px -12% 0px' }
      );
      Array.prototype.forEach.call(cues, function (cue) {
        cueObserver.observe(cue);
      });
    }

    // Sections re-render in the theme editor; re-scan so they do not stay hidden.
    document.addEventListener('shopify:section:load', function (event) {
      observe(event.target);
    });

    // Last-resort guard: if anything above silently failed to reveal on-screen
    // content, stop hiding it.
    window.setTimeout(function () {
      var stillHidden = document.querySelectorAll('.pa-reveal:not(.pa-reveal--in)');
      if (stillHidden.length === 0) return;
      var anyInView = Array.prototype.some.call(stillHidden, function (el) {
        var box = el.getBoundingClientRect();
        return box.top < window.innerHeight && box.bottom > 0;
      });
      if (anyInView) revealAll();
    }, 3000);
  } catch (e) {
    revealAll();
  }
})();

/*
 * Palo Alto — press to see the room.
 *
 * A phone has no hover, so a finger resting on a product card stands in for it:
 * the in-situ photograph appears under the finger and fades back when it lifts.
 * CSS alone cannot do this. `:hover` on touch latches after a tap and never
 * clears, and `:active` is gone in the moment it takes a tap to become a
 * navigation -- long enough to flicker, never long enough to look at.
 *
 * Three gestures share the same finger-down, and the timings here are what keep
 * them apart:
 *
 *   scroll   finger lands and travels        never reveals anything
 *   tap      finger lands and lifts fast     opens the product, as always
 *   rest     finger lands and stays          reveals the room, opens nothing
 *
 * The settle delay is what makes the first one work. Revealing the instant a
 * finger touches down would flash a photograph on every single swipe of a
 * collection page, because a scroll begins by planting a finger on whatever
 * happens to be under it.
 *
 * Same safety property as the reveal above: this is an enhancement over a card
 * that already works. Anything that throws leaves the card exactly as it was.
 */
(function () {
  'use strict';

  // Both card families: our own snippet, and the Dawn card that the collection,
  // search and related-products grids render.
  var CARD_SELECTOR = '.pa-card, .card-wrapper';
  // The photograph the press exists to reveal. A card without one -- an article
  // card, or a product whose in-situ shot has not landed -- is left alone
  // entirely rather than given a press that does nothing.
  var SITU_SELECTOR = '.pa-card__image--situ, .media--hover-effect > img + img';
  var PRESSED = 'pa-pressed';
  // How long a finger must stay still before this is a rest rather than the
  // start of a scroll. Short enough to feel like the photograph was already
  // there, long enough that swiping past a grid never flickers.
  var SETTLE_MS = 140;
  // Past this, a press was a look rather than a slow tap, so the click it
  // produces is swallowed and the grid keeps its scroll position. 500ms is what
  // iOS and Android already treat as a long press, so it matches the gesture
  // people have. A median tap is well under 150ms, leaving generous room for an
  // unhurried one to still open the product -- and by the time the threshold
  // passes the shopper has been looking at the room shot for 360ms, which is
  // what makes the swallowed click read as a peek rather than a dead tap.
  var HOLD_MS = 500;
  // A finger that travels this far is scrolling the grid, not resting on a card.
  var MOVE_TOLERANCE_PX = 10;
  // iOS fires its synthetic click a few hundred ms after the finger lifts.
  var CLICK_WINDOW_MS = 700;

  try {
    if (!('ontouchstart' in window)) return;
    if (!document.documentElement.closest) return;

    // The card under the finger right now. Set from the moment of touchdown,
    // which is before the photograph is revealed -- the OS long-press sheet has
    // to be headed off during the settle window too, not just after it.
    var activeCard = null;
    var revealedCard = null;
    var settleTimer = 0;
    var startedAt = 0;
    var startX = 0;
    var startY = 0;
    var swallowClickUntil = 0;
    var heldCard = null;

    function cardFor(target) {
      if (!target || !target.closest) return null;
      var card = target.closest(CARD_SELECTOR);
      if (!card || !card.querySelector(SITU_SELECTOR)) return null;
      return card;
    }

    function clearPress() {
      if (settleTimer) {
        window.clearTimeout(settleTimer);
        settleTimer = 0;
      }
      if (revealedCard) {
        revealedCard.classList.remove(PRESSED);
        revealedCard = null;
      }
      activeCard = null;
    }

    document.addEventListener(
      'touchstart',
      function (event) {
        clearPress();
        // A new touch is a new gesture, even if the browser did not dispatch
        // a synthetic click for the preceding hold.
        swallowClickUntil = 0;
        heldCard = null;
        // A second finger is a pinch or a two-handed scroll, not a press.
        if (event.touches.length !== 1) return;
        var card = cardFor(event.target);
        if (!card) return;

        activeCard = card;
        startedAt = Date.now();
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;

        settleTimer = window.setTimeout(function () {
          settleTimer = 0;
          if (!activeCard) return;
          revealedCard = activeCard;
          revealedCard.classList.add(PRESSED);
        }, SETTLE_MS);
      },
      { passive: true }
    );

    document.addEventListener(
      'touchmove',
      function (event) {
        if (!activeCard) return;
        var touch = event.touches[0];
        if (!touch) return;
        if (
          Math.abs(touch.clientX - startX) > MOVE_TOLERANCE_PX ||
          Math.abs(touch.clientY - startY) > MOVE_TOLERANCE_PX
        ) {
          // Hand the gesture back to the scroller, and let any click through: a
          // finger dragged off a card was never opening it anyway.
          clearPress();
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'touchend',
      function () {
        if (!activeCard) return;
        if (Date.now() - startedAt >= HOLD_MS) {
          swallowClickUntil = Date.now() + CLICK_WINDOW_MS;
          heldCard = activeCard;
        }
        clearPress();
      },
      { passive: true }
    );

    document.addEventListener('touchcancel', clearPress, { passive: true });

    // Capture phase, so the card's link never sees the click at all.
    document.addEventListener(
      'click',
      function (event) {
        if (Date.now() > swallowClickUntil) return;
        if (event.detail === 0) return;
        if (cardFor(event.target) !== heldCard) return;
        // One click per hold. Whatever comes after is a real tap and must open.
        swallowClickUntil = 0;
        heldCard = null;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Only while a finger is actually down on a card, so a desktop right-click
    // anywhere -- and a long press on anything that is not a card -- is left
    // completely alone.
    document.addEventListener('contextmenu', function (event) {
      if (!activeCard) return;
      event.preventDefault();
    });
  } catch (e) {
    // A card that does not do the extra thing is still a card that works.
  }
})();
